import { Box, Card, CardContent, CircularProgress, IconButton, Tab, Tabs, Tooltip, Typography, useTheme } from '@mui/material'
import { useEffect, useMemo, useState } from 'react'
import { IChangelog, IComment, ITask, IWorkFlow, WorkflowStatus } from '../../definitions/Workflow'
import Tile from '../layout/Tile'
import Comments from './Comments'
import CurrentTask from './CurrentTask'
import TaskTimeline from './TaskTimeline'
import ApiClient from '../../backend/ApiClient'
import { snackbar } from '../services/SnackbarService'
import useInvalidate from '../hooks/useInvalidate'
import { buildQueryKey } from '../hooks/useQuery'
import CloseIcon from '@mui/icons-material/Close'
import PictureInPictureAltIcon from '@mui/icons-material/PictureInPictureAlt'
import { pip } from '../services/PiPService'
import API_URLS from '../../backend/ApiUrls'
import WorkflowInfo from './WorkflowInfo'
import WorkflowHistory from './WorkflowHistory'
import WorkflowManager from '../../managers/WorkflowManager'
import Helper from '../../helpers/Helper'

interface P {
    originalWorkflow: IWorkFlow
    onClose: () => void
}

export default function WorkflowModal({ originalWorkflow, onClose }: P) {
    const theme = useTheme()
    const [tabIndex, setTabIndex] = useState(0)
    const [workflow, setWorkflow] = useState(originalWorkflow)
    const [tasks, setTasks] = useState(originalWorkflow.tasks)
    const [loading, setLoading] = useState(false)
    const invalidate = useInvalidate({ queryKey: buildQueryKey(API_URLS.os.getAllWorkflows + '/*') })

    useEffect(() => {
        pip.close()
    }, [originalWorkflow])

    const updateWorkflow = async (updatedFields: Partial<IWorkFlow>, newChangelogs: IChangelog[]) => {
        setLoading(true)
        try {
            const updatedWorkflow = { ...workflow, ...updatedFields }
            if (newChangelogs.length > 0) {
                updatedWorkflow.changelogs = [...(updatedWorkflow.changelogs || []), ...newChangelogs]
            }
            setWorkflow(updatedWorkflow)

            await ApiClient.updateWorkflow(updatedWorkflow)
            snackbar.show({ severity: 'success', text: 'Workflow updated successfully' })
            invalidate()
        } catch (error) {
            console.error('Error updating workflow', error)
            snackbar.show({ severity: 'error', text: 'Failed to update workflow' })
        } finally {
            setLoading(false)
        }
    }

    const openPiP = () => {
        pip.open(
            {
                metadata: {
                    workflow: workflow
                },
                content: (
                    <>
                        <Box sx={{ padding: 1, visibility: 'visible', flex: 1, maxHeight: 'calc(var(--unit)*60)' }}>
                            <Typography
                                fontWeight='bold'
                                variant='h5'>{workflow?.title}
                            </Typography>
                            <Typography variant='body2' fontSize={18}>
                                {`for: ${(Helper.hasVal(workflow.contract) && workflow.contract.name.length > 0) ? workflow.contract.name : 'X contract'}`}
                            </Typography>
                            <CurrentTask
                                tasks={reverseScheduleTasks}
                                isPiP={true}
                                onChange={onChangeTasks}
                            />
                        </Box>
                    </>
                )
            }
        )
        onClose()
    }

    const onChangeComments = (newComments: IComment[], newChangelogs: IChangelog[]) => {
        const updatedFields = { comments: newComments }
        updateWorkflow(updatedFields, newChangelogs)
    }

    const onChangeTasks = (newTasks: ITask[], newChangelogs: IChangelog[]) => {
        const now = new Date()
        const allTasksCompleted: boolean = newTasks.every(task => task.status === 'completed')
        const newStatus: WorkflowStatus = allTasksCompleted ? 'completed' : 'open'

        const updatedFields: Partial <IWorkFlow> = { tasks: newTasks }
        if (workflow.status !== newStatus) {
            updatedFields.status = newStatus
            updatedFields.completedAt = now.toISOString()
        }
        setTasks(newTasks)
        updateWorkflow(updatedFields, newChangelogs)
    }

    const taskMap = useMemo(() => new Map(tasks.map(t => [t.uuid, t])), [tasks])

    const scheduledTasks = useMemo<{ task: ITask, startDate: Date, endDate: Date }[]>(() => {
        if (!tasks || tasks.length === 0) return []
        const sortedTasks: ITask[] = []
        const visited = new Set<string>()

        const visit = (task: ITask) => {
            if (visited.has(task.uuid)) return
            visited.add(task.uuid)

            if (task.dependencies && task.dependencies.length > 0) {
                task.dependencies.forEach(depId => {
                    const depTask = taskMap.get(depId.uuid)
                    if (depTask) visit(depTask)
                })
            }
            sortedTasks.push(task)
        }

        tasks.forEach(task => {
            if (!visited.has(task.uuid)) visit(task)
        })

        const scheduledTasks: { task: ITask, startDate: Date, endDate: Date }[] = []
        let currentStartDate = new Date(sortedTasks[0]?.startedAt ?? Date.now())
        currentStartDate.setHours(0, 0, 0, 0)

        sortedTasks.forEach(task => {
            const startDate = currentStartDate
            const endDate = new Date(startDate.getTime())

            if (task.status === 'completed' && task.completedAt) {
                const completedAt = new Date(task.completedAt)
                scheduledTasks.push({ task, startDate, endDate })
                currentStartDate = new Date(completedAt)
            } else {
                let daysAdded = 0
                while (daysAdded < task.daysToComplete) {
                    endDate.setDate(endDate.getDate() + 1)
                    if (WorkflowManager.isWorkingDay(endDate)) daysAdded++
                }

                scheduledTasks.push({ task, startDate, endDate })
                const extraDays = Math.ceil(task.daysToComplete * 0.2)
                currentStartDate = new Date(endDate.getTime())
                currentStartDate.setDate(currentStartDate.getDate() + extraDays)
                while (!WorkflowManager.isWorkingDay(currentStartDate)) {
                    currentStartDate.setDate(currentStartDate.getDate() + 1)
                }
            }
        })

        return scheduledTasks
    }, [tasks, taskMap])

    const workflowDueDate = workflow.dueDate

    const reverseScheduleTasks = useMemo<{ task: ITask, startDate: Date, endDate: Date }[]>(() => {
        if (!tasks || tasks.length === 0) return []

        const sortedTasks: ITask[] = []
        const visited = new Set<string>()

        const visit = (task: ITask) => {
            if (visited.has(task.uuid)) return
            visited.add(task.uuid)

            if (task.dependencies && task.dependencies.length > 0) {
                task.dependencies.forEach(depId => {
                    const depTask = taskMap.get(depId.uuid)
                    if (depTask) visit(depTask)
                })
            }
            sortedTasks.push(task)
        }

        tasks.forEach(task => {
            if (!visited.has(task.uuid)) visit(task)
        })

        const scheduledTasks: { task: ITask, startDate: Date, endDate: Date }[] = []
        let currentEndDate = new Date(workflowDueDate)
        currentEndDate.setHours(23, 59, 59, 999)

        for (let i = sortedTasks.length - 1; i >= 0; i--) {
            const task = sortedTasks[i]

            const endDate = new Date(currentEndDate.getTime())
            const startDate = new Date(endDate.getTime())
            const daysToComplete = Number(task.daysToComplete)
            let daysAdded = 0
            while (daysAdded < daysToComplete) {
                startDate.setDate(startDate.getDate() - 1)
                if (WorkflowManager.isWorkingDay(startDate)) daysAdded++
            }
            scheduledTasks.unshift({ task, startDate, endDate })
            currentEndDate = new Date(startDate.getTime())
            const extraDays = Math.ceil(0.2 * task.daysToComplete) + 1
            currentEndDate.setDate(currentEndDate.getDate() - extraDays)
        }
        return scheduledTasks
    }, [tasks, taskMap, workflowDueDate])

    const marginModalVertical = 20

    return (
        <>
            <Box
                maxWidth={780}
                sx={{
                    margin: `${marginModalVertical}px auto ${marginModalVertical}px`,
                    transition: 'all 0.3s ease',
                    borderRadius: '8px',
                    height: '-webkit-fill-available'
                }}>
                <Card style={{ height: '100%', position: 'relative', display: 'flex', flexDirection: 'column', paddingBottom: theme.spacing(2) }}>
                    <Box display='flex' justifyContent='space-between' alignItems='center' p={2}>
                        <Typography variant='h5'>
                            {workflow?.title}
                        </Typography>
                        <Box>
                            <Tooltip title='Picture-in-Picture'>
                                <IconButton onClick={openPiP} size='small' disabled={loading}>
                                    {loading ? <CircularProgress size={20} /> : <PictureInPictureAltIcon />}
                                </IconButton>
                            </Tooltip>
                            <Tooltip title='Close'>
                                <IconButton onClick={onClose} size='small'>
                                    <CloseIcon />
                                </IconButton>
                            </Tooltip>
                        </Box>
                    </Box>
                    <CardContent style={{ paddingTop: 0, paddingRight: theme.spacing(3), paddingLeft: theme.spacing(3), paddingBottom: theme.spacing(3), height: '100%', display: 'flex', flexDirection: 'column' }}>
                        <Tabs value={tabIndex} onChange={(_, val) => setTabIndex(val)} style={{ marginTop: theme.spacing(3) }}>
                            <Tab label='Current Task' />
                            <Tab label='All Tasks' />
                            <Tab label='Comments' />
                            <Tab label='Activity' />
                            <Tab label='Workflow Info' />
                        </Tabs>
                        <Box sx={{ display: 'flex', overflow: 'auto', height: '100%', marginBottom: (marginModalVertical * 2) + 'px' }}>
                            <Tile.Column>
                                {tabIndex === 0 && (
                                    <CurrentTask
                                        tasks={tasks.some(x => x.status === 'completed') ? scheduledTasks : reverseScheduleTasks}
                                        onChange={onChangeTasks}
                                    />
                                )}
                                {tabIndex === 1 &&
                                    <TaskTimeline tasks={tasks.some(x => x.status === 'completed') ? scheduledTasks : reverseScheduleTasks} taskMap={taskMap} />}
                                {tabIndex === 2 &&
                                    <Comments comments={workflow.comments} onChange={onChangeComments} />}
                                {tabIndex === 3 && (
                                    <WorkflowHistory workflow={workflow} />
                                )}
                                {tabIndex === 4 && (
                                    <WorkflowInfo workflow={workflow} />
                                )}
                            </Tile.Column>
                        </Box>
                    </CardContent>
                </Card>
            </Box>
        </>
    )
}
